import { i18n, setupI18n, Messages } from '@lingui/core';

import { importWithRetry } from '../utils/import';
import { getLocale } from './typesProxy';
import locales from './frontend/locales';

const defaultLanguage = 'en';

const loadLanguageMessage = async (language: string): Promise<Messages> => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { messages } = await importWithRetry(() => import(`../locales/${language}/messages.js`));
  return messages as Messages;
};

/**
 * We do a dynamic import of just the catalog that we need
 * @param langCode any language code string
 */
export const activateLanguage = async (langCode?: string | null): Promise<void> => {
  try {
    const language = langCode || defaultLanguage;
    const messages = await loadLanguageMessage(language);
    i18n.load(language, messages);
    i18n.activate(language);
  } catch (e) {
    console.error(e);
  }
};

export const getLocalI18n = async (langCode?: string | null): Promise<typeof i18n> => {
  try {
    const language = langCode || defaultLanguage;
    const messages = await loadLanguageMessage(language);
    const i18n = setupI18n();
    // i18n.loadLocaleData(pluralMappings);
    i18n.load(language, messages);
    i18n.activate(language);
    return i18n;
  } catch {
    // We have a case that locale message loading failed so use the global i18n
    return i18n;
  }
};

export const getTranslatedText = async (
  text: string,
  langCode?: string | null
): Promise<string> => {
  const i18n = await getLocalI18n(langCode);
  /* lingui-extract-ignore */
  return i18n._(text);
};

export function isLangRtl(lang: string): boolean {
  const rtlLangs = ['ar', 'he'];
  return rtlLangs.includes(lang);
}

export function langDirClassname(lang?: string | null, forWidget?: boolean): string {
  let prefix = '';
  if (forWidget) prefix = 'ewebinar-';

  return `${prefix}${lang && isLangRtl(lang) ? 'rtl' : 'ltr'}`;
}

export interface LocaleOptions {
  languageCodeOnly?: boolean;
}

export const getLocaleForLang = (lang: string, clientLocale: string = getLocale()): string => {
  const localeParts = clientLocale.split('-');
  if (localeParts.length !== 2) {
    return lang;
  }

  const locale = [lang, localeParts[1]].filter((s) => s).join('-');
  if (!locales[locale]) {
    return lang;
  }

  return locale;
};
